import { useState, useEffect, useRef, useMemo } from "react";
import { JobQuotePicker } from "@/components/JobQuotePicker";
import { createPortal } from "react-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Minus, X, Scissors, Ruler, Check, Square, CheckSquare, Printer, Download, Share2, History, RotateCcw, Copy, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TechCapabilityWarning } from "@/components/TechCapabilityWarning";
import { jsPDF } from "jspdf";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function JobDialog({ open, onOpenChange, job, preselectedCustomer, onSuccess, isAdmin = false }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: "",
    job_type: "on_site",
    date: "",
    time: "",
    duration: "",
    assignedEmployee: "",
    assignedEmployee2: "",
    status: "Open",
    notes: "",
    screen_count: ""
  });
  const [loading, setLoading] = useState(false);
  
  // Screen measurements state
  const [screenMeasurements, setScreenMeasurements] = useState([]);
  const [newWidth, setNewWidth] = useState("");
  const [newHeight, setNewHeight] = useState("");
  const [pullTabSide, setPullTabSide] = useState("height"); // "width" or "height"
  // Screen DOOR orders — separate fabrication track with its own fields
  // (type/color/handle/mesh) and its own "Copy order" text format.
  const [screenDoors, setScreenDoors] = useState([]);
  const [newDoor, setNewDoor] = useState({
    width: "",
    height: "",
    type: "Century",
    color: "White",
    handle: "right",
    mesh: "Regular",
  });
  // Pick Up jobs: optional pickup instructions line + tracking the
  // "Ready for pickup" SMS send state so the button toggles correctly
  // and we can show the sent timestamp.
  const [pickupInstructions, setPickupInstructions] = useState(
    "Screen will be by the front door.\n\n**No need to knock or ring**"
  );
  const [pickupNotifiedAt, setPickupNotifiedAt] = useState(null);
  const [sendingPickupSms, setSendingPickupSms] = useState(false);
  const [cutList, setCutList] = useState([]);
  const [showCutList, setShowCutList] = useState(false);
  const [generatingCutList, setGeneratingCutList] = useState(false);
  // "longest" = simple sort desc (current behavior).
  // "optimize" = pack cuts into 144" sticks (First-Fit-Decreasing) so
  // the fabricator sees exactly how many sticks to grab and how much
  // drop is left on each one.
  const [cutMode, setCutMode] = useState("longest");
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  // Track if we've initialized the form to prevent re-initialization
  const [initialized, setInitialized] = useState(false);
  
  // Track unsaved changes and confirmation dialog
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const initialMeasurementsRef = useRef([]);
  const initialFormRef = useRef({});
  const printCutListRef = useRef(null);

  // Measurement audit history (for accidental-overwrite recovery)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySnapshots, setHistorySnapshots] = useState([]);
  const [restoringIndex, setRestoringIndex] = useState(null);

  // --- Draft auto-save & save reliability ---------------------------------
  // Unsaved measurements (typed in but never sent to the server) are
  // mirrored to localStorage on every change. If the app is force-killed,
  // Safari kills the tab, or the tech taps Close by mistake, the next time
  // they open this same job they'll be prompted to restore the draft.
  //
  // Draft key strategy:
  //   - existing job → `job_meas_draft_<job_id>`
  //   - new job with preselected customer → `job_meas_draft_new_<customer_id>`
  //   - brand new job picker → `job_meas_draft_new_pending` (rare)
  const draftKey = (() => {
    if (job?.job_id) return `job_meas_draft_${job.job_id}`;
    if (preselectedCustomer?.customer_id) return `job_meas_draft_new_${preselectedCustomer.customer_id}`;
    return `job_meas_draft_new_pending`;
  })();
  const [draftRestoreOpen, setDraftRestoreOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  // Save state: 'idle' | 'saving' | 'failed'. 'idle' covers success too
  // because we close on success — `failed` keeps the dialog open so the
  // tech can hit retry without losing their typed-in measurements.
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchCustomers();
      fetchTechs();
    } else {
      // Reset initialized flag when dialog closes
      setInitialized(false);
    }
  }, [open]);

  useEffect(() => {
    // Only initialize form when dialog opens, not on every render
    if (!open || initialized) return;
    
    if (job) {
      const initialForm = {
        customer_id: job.customer_id || "",
        job_type: job.job_type || "on_site",
        date: job.date || "",
        time: job.time || "",
        duration: job.duration || "",
        assignedEmployee: job.assignedEmployee || "",
        assignedEmployee2: job.assignedEmployee2 || "",
        status: job.status || "Open",
        notes: job.notes || "",
        screen_count: job.screen_count || "",
        quote_lead_id: job.quote_lead_id || ""
      };
      setFormData(initialForm);
      const initialMeasurements = job.screen_measurements || [];
      setScreenMeasurements(initialMeasurements);
      setScreenDoors(job.screen_door_measurements || []);
      setPickupInstructions(
        job.pickup_instructions ||
        "Screen will be by the front door.\n\n**No need to knock or ring**"
      );
      setPickupNotifiedAt(job.pickup_notified_at || null);
      setCutList(job.cut_list || []);
      setShowCutList((job.cut_list || []).length > 0);
      
      // Store initial values for change detection
      initialFormRef.current = initialForm;
      initialMeasurementsRef.current = JSON.parse(JSON.stringify(initialMeasurements));
    } else if (preselectedCustomer) {
      const initialForm = {
        customer_id: preselectedCustomer.customer_id,
        job_type: "on_site",
        date: "",
        time: "",
        duration: "",
        assignedEmployee: "",
        assignedEmployee2: "",
        status: "Open",
        notes: "",
        screen_count: ""
      };
      setFormData(initialForm);
      setScreenMeasurements([]);
      setScreenDoors([]);
      setCutList([]);
      setShowCutList(false);
      
      initialFormRef.current = initialForm;
      initialMeasurementsRef.current = [];
    } else {
      const initialForm = {
        customer_id: "",
        job_type: "on_site",
        date: "",
        time: "",
        duration: "",
        assignedEmployee: "",
        assignedEmployee2: "",
        status: "Open",
        notes: "",
        screen_count: ""
      };
      setFormData(initialForm);
      setScreenMeasurements([]);
      setScreenDoors([]);
      setCutList([]);
      setShowCutList(false);
      
      initialFormRef.current = initialForm;
      initialMeasurementsRef.current = [];
    }
    // Reset new measurement inputs
    setNewWidth("");
    setNewHeight("");
    setPullTabSide("height");
    setInitialized(true);
  }, [job, preselectedCustomer, open, initialized]);

  // On open, check localStorage for a draft that differs from the saved
  // measurements. If found, ask the tech if they want to restore it.
  useEffect(() => {
    if (!open || !initialized) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || !Array.isArray(draft.measurements)) return;
      const savedJson = JSON.stringify(initialMeasurementsRef.current || []);
      const draftJson = JSON.stringify(draft.measurements);
      if (savedJson === draftJson) {
        // Draft matches what's already saved — nothing to restore, clean it up.
        localStorage.removeItem(draftKey);
        return;
      }
      setPendingDraft(draft);
      setDraftRestoreOpen(true);
    } catch (err) {
      console.warn("Could not parse measurement draft:", err);
      localStorage.removeItem(draftKey);
    }
  }, [open, initialized, draftKey]);

  // Auto-save measurements to localStorage on every change so they survive
  // app crashes, tab kills, force-quits, etc. We only persist drafts when
  // they DIFFER from the saved baseline — no point hoarding identical state.
  useEffect(() => {
    if (!open || !initialized) return;
    try {
      const currentJson = JSON.stringify(screenMeasurements);
      const savedJson = JSON.stringify(initialMeasurementsRef.current || []);
      if (currentJson === savedJson) {
        localStorage.removeItem(draftKey);
        return;
      }
      localStorage.setItem(draftKey, JSON.stringify({
        measurements: screenMeasurements,
        saved_at: new Date().toISOString(),
        job_id: job?.job_id || null,
      }));
    } catch (err) {
      // localStorage can throw in Private Browsing / quota — fail silently
      console.warn("Could not write measurement draft:", err);
    }
  }, [screenMeasurements, draftKey, initialized, open, job?.job_id]);

  const restoreDraftMeasurements = () => {
    if (pendingDraft?.measurements) {
      setScreenMeasurements(pendingDraft.measurements);
      setCutList([]); // cut list is derived, force a regenerate
      toast.success(`Restored ${pendingDraft.measurements.length} draft measurement${pendingDraft.measurements.length === 1 ? "" : "s"}`);
    }
    setDraftRestoreOpen(false);
    setPendingDraft(null);
  };

  const discardDraftMeasurements = () => {
    try { localStorage.removeItem(draftKey); } catch (_err) { /* localStorage may be unavailable in Private Browsing */ }
    setDraftRestoreOpen(false);
    setPendingDraft(null);
  };

  // Check if there are unsaved changes (especially measurements)
  const hasUnsavedChanges = () => {
    // Check if measurements have changed
    const currentMeasurements = JSON.stringify(screenMeasurements);
    const initialMeasurements = JSON.stringify(initialMeasurementsRef.current);
    
    if (currentMeasurements !== initialMeasurements) {
      return true;
    }
    
    // Check if any form fields have changed (for new jobs or edits)
    const initialForm = initialFormRef.current;
    if (formData.notes !== initialForm.notes ||
        formData.date !== initialForm.date ||
        formData.time !== initialForm.time ||
        formData.screen_count !== initialForm.screen_count) {
      return true;
    }
    
    return false;
  };

  // Radix Dialog's `onOpenChange(nextOpen)` fires with `false` when the
  // user clicks the X / hits Esc / clicks the backdrop, and `true` when
  // something inside would re-open. Old code inverted the parameter name
  // (`shouldClose`) and re-called `onOpenChange(true)` on close attempts,
  // which effectively cancelled every close — the X button did nothing.
  const handleCloseAttempt = (nextOpen) => {
    if (nextOpen) {
      // Nothing to intercept when opening; pass through.
      onOpenChange(true);
      return;
    }
    // User is trying to close — if they have unsaved measurements /
    // form edits, show the confirmation dialog first. Otherwise close.
    if (hasUnsavedChanges()) {
      setShowCloseConfirm(true);
    } else {
      onOpenChange(false);
    }
  };

  // Confirm close without saving
  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onOpenChange(false);
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/customers`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    }
  };

  const fetchTechs = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/techs`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTechs(response.data);
    } catch (error) {
      toast.error("Failed to fetch techs");
    }
  };

  const openMeasurementHistory = async () => {
    if (!job?.job_id) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/jobs/${job.job_id}/measurements/history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setHistorySnapshots(res.data?.history || []);
    } catch (err) {
      console.error("Failed to load measurement history:", err);
      toast.error("Failed to load measurement history");
      setHistorySnapshots([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const restoreMeasurementSnapshot = async (index) => {
    if (!job?.job_id) return;
    if (!window.confirm("Restore these measurements? The current measurements will be archived (still recoverable).")) return;
    setRestoringIndex(index);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(
        `${API}/jobs/${job.job_id}/measurements/restore`,
        { history_index: index },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const restored = res.data?.screen_measurements || [];
      setScreenMeasurements(restored);
      initialMeasurementsRef.current = JSON.parse(JSON.stringify(restored));
      toast.success(`Restored ${restored.length} measurement${restored.length === 1 ? "" : "s"}`);
      setHistoryOpen(false);
      // Bubble up so the parent re-fetches the job
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Restore failed:", err);
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setRestoringIndex(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaveState("saving");
    setSaveError(null);

    try {
      const token = localStorage.getItem('session_token');
      
      // Clean up the data - remove empty strings and convert to proper types
      const submitData = {
        customer_id: formData.customer_id,
        job_type: formData.job_type || "on_site",
        date: formData.date || null,
        time: formData.time || null,
        duration: formData.duration ? parseInt(formData.duration) : null,
        assignedEmployee: formData.assignedEmployee || null,
        assignedEmployee2: formData.assignedEmployee2 || null,
        status: formData.status || null,
        notes: formData.notes || null,
        screen_measurements: screenMeasurements,
        screen_door_measurements: screenDoors,
        pickup_instructions: formData.job_type === "pickup" ? pickupInstructions : null,
        cut_list: cutList.length > 0 ? cutList : null,
        screen_count: formData.screen_count ? parseInt(formData.screen_count) : null,
        ...(isAdmin ? { quote_lead_id: formData.quote_lead_id || "" } : {})
      };

      // 15s ceiling so a flaky cell connection doesn't hang forever — better
      // to surface "Save failed, retry" than spin indefinitely.
      const reqConfig = {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 15000,
      };

      let savedJobId = job?.job_id;
      if (job) {
        await axios.put(`${API}/jobs/${job.job_id}`, submitData, reqConfig);
      } else {
        const createRes = await axios.post(`${API}/jobs`, submitData, reqConfig);
        savedJobId = createRes.data?.job_id || savedJobId;
      }

      // Verify the server actually has what we just sent — catches silent
      // partial writes on bad signal where the request "succeeded" but
      // measurements never made it.
      const sentCount = screenMeasurements.length;
      if (savedJobId && sentCount > 0) {
        try {
          const verify = await axios.get(`${API}/jobs/${savedJobId}`, reqConfig);
          const persistedCount = (verify.data?.screen_measurements || []).length;
          if (persistedCount !== sentCount) {
            throw new Error(
              `Save verification failed: sent ${sentCount} measurements but server has ${persistedCount}.`
            );
          }
        } catch (verifyErr) {
          // If the verify GET itself failed (network), trust the PUT — but log it
          if (verifyErr.message?.startsWith("Save verification failed")) throw verifyErr;
          console.warn("Could not verify save — assuming success:", verifyErr.message);
        }
      }

      // Clean up the draft now that we know the server has the data.
      try { localStorage.removeItem(draftKey); } catch (_err) { /* localStorage may be unavailable in Private Browsing */ }

      setSaveState("idle");
      toast.success(job ? "Job updated" : "Job created");
      onSuccess();
    } catch (error) {
      console.error("Job save error:", error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail;
      let msg;
      if (typeof errorMsg === 'string') msg = errorMsg;
      else if (Array.isArray(errorMsg)) msg = errorMsg[0]?.msg || "Failed to save job";
      else if (error.code === "ECONNABORTED") msg = "Save timed out — check your signal and try again.";
      else if (error.message?.startsWith("Save verification failed")) msg = error.message;
      else msg = "Failed to save job. Your measurements are still here — tap Retry.";

      setSaveState("failed");
      setSaveError(msg);
      toast.error(msg);
      // Intentionally do NOT call onSuccess() — keep the dialog open so the
      // tech can retry without losing their typed-in measurements.
    } finally {
      setLoading(false);
    }
  };

  // Screen measurement handlers
  
  // Parse fractional input like "20 1/4" or "20.25" to decimal. Strips
  // stray characters (`"`, `*` pull-tab marker, non-breaking spaces) BEFORE
  // matching so a measurement typed with a trailing inch mark or pasted
  // with a hidden character still parses. The strict regex used to fail
  // silently on those, dropping the whole screen from the cut list — one
  // customer saw 20 cuts instead of 36 because a single row had a stray
  // char in its width value.
  const parseFractionalInput = (input) => {
    if (!input && input !== 0) return null;
    // Coerce to string, drop inch marks / asterisks / non-breaking spaces,
    // collapse internal whitespace to a single space, then trim.
    const str = input
      .toString()
      .replace(/["'*\u00A0]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!str) return null;

    // Mixed fraction like "20 1/4"
    const mixedMatch = str.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1]);
      const numerator = parseInt(mixedMatch[2]);
      const denominator = parseInt(mixedMatch[3]);
      if (denominator === 0) return null;
      return whole + (numerator / denominator);
    }

    // Simple fraction like "1/4"
    const fractionMatch = str.match(/^(\d+)\/(\d+)$/);
    if (fractionMatch) {
      const numerator = parseInt(fractionMatch[1]);
      const denominator = parseInt(fractionMatch[2]);
      if (denominator === 0) return null;
      return numerator / denominator;
    }

    // Otherwise parse as decimal
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  };

  // Format decimal inches as fraction string (e.g., 20.25 -> "20 1/4")
  // Supports 16ths for precision
  const formatAsFraction = (decimal) => {
    if (decimal === null || decimal === undefined) return "";
    
    const whole = Math.floor(decimal);
    const remainder = decimal - whole;
    
    // Common fractions in inches (including 16ths)
    const fractions = [
      { value: 0, str: "" },
      { value: 1/16, str: "1/16" },
      { value: 1/8, str: "1/8" },
      { value: 3/16, str: "3/16" },
      { value: 1/4, str: "1/4" },
      { value: 5/16, str: "5/16" },
      { value: 3/8, str: "3/8" },
      { value: 7/16, str: "7/16" },
      { value: 1/2, str: "1/2" },
      { value: 9/16, str: "9/16" },
      { value: 5/8, str: "5/8" },
      { value: 11/16, str: "11/16" },
      { value: 3/4, str: "3/4" },
      { value: 13/16, str: "13/16" },
      { value: 7/8, str: "7/8" },
      { value: 15/16, str: "15/16" },
    ];
    
    // Find closest fraction
    let closest = fractions[0];
    let minDiff = Math.abs(remainder);
    
    for (const frac of fractions) {
      const diff = Math.abs(remainder - frac.value);
      if (diff < minDiff) {
        minDiff = diff;
        closest = frac;
      }
    }
    
    if (closest.str === "") {
      return whole.toString();
    }
    return whole > 0 ? `${whole} ${closest.str}` : closest.str;
  };

  // Bin-pack the cut list into 144" sticks using First-Fit-Decreasing.
  // Each stick is a length of aluminum stock; we want to minimize the
  // number of sticks (and therefore drop/waste). Cuts are expanded into
  // individual pieces, sorted longest→shortest, then each piece is
  // placed on the first stick that has room. FFD gets within ~11% of
  // optimal for practical cut lists — plenty good for a screen shop
  // and O(n·k) fast.
  const STICK_LENGTH = 144;
  const packCutsIntoSticks = (list) => {
    // Expand into individual cuts
    const pieces = [];
    for (const item of list) {
      const size = Number(item.size) || 0;
      const count = Number(item.count) || 0;
      for (let i = 0; i < count; i += 1) pieces.push(size);
    }
    pieces.sort((a, b) => b - a);

    const sticks = []; // [{ cuts: number[], remaining: number }]
    for (const piece of pieces) {
      if (piece <= 0 || piece > STICK_LENGTH) continue;
      let placed = false;
      for (const s of sticks) {
        if (s.remaining >= piece) {
          s.cuts.push(piece);
          s.remaining = Math.round((s.remaining - piece) * 16) / 16;
          placed = true;
          break;
        }
      }
      if (!placed) {
        sticks.push({
          cuts: [piece],
          remaining: Math.round((STICK_LENGTH - piece) * 16) / 16,
        });
      }
    }
    return sticks;
  };

  // Memoize the packed layout so the render doesn't recompute on every
  // keystroke elsewhere in the dialog. Cheap for realistic job sizes.
  const packedSticks = useMemo(
    () => (cutMode === "optimize" ? packCutsIntoSticks(cutList) : []),
    [cutMode, cutList]
  );
  const totalWaste = packedSticks.reduce((sum, s) => sum + s.remaining, 0);

  // --- Screen door helpers ------------------------------------------------
  // Parses width/height from either decimal ("49.25") or mixed-fraction
  // ("49 1/4") input, pushes a new door onto `screenDoors`, and resets
  // the input row. Validates like `addMeasurement` does for windows so
  // the two tracks feel the same.
  const addScreenDoor = () => {
    if (!newDoor.width || !newDoor.height) {
      toast.error("Enter width and height for the screen door");
      return;
    }
    const w = parseFractionalInput(newDoor.width);
    const h = parseFractionalInput(newDoor.height);
    if (w === null || h === null || w <= 0 || h <= 0) {
      toast.error("Enter valid dimensions (e.g., 49 1/4 or 94)");
      return;
    }
    const entry = {
      width: newDoor.width,
      height: newDoor.height,
      width_decimal: w,
      height_decimal: h,
      type: newDoor.type,
      color: newDoor.color || "",
      handle: newDoor.handle,
      mesh: newDoor.mesh,
    };
    setScreenDoors([...screenDoors, entry]);
    // Keep type/color/handle/mesh as-is so adding multiple matching
    // doors is one tap each; just clear the dimensions.
    setNewDoor({ ...newDoor, width: "", height: "" });
  };

  const removeScreenDoor = (idx) => {
    setScreenDoors(screenDoors.filter((_, i) => i !== idx));
  };

  const formatDoorDimensions = (door) => {
    // Prefer the exact string the tech typed; fall back to the decimal
    // reconstruction so pasted/imported doors still render cleanly.
    const w =
      typeof door.width === "string" && door.width.trim()
        ? door.width.trim()
        : formatAsFraction(door.width_decimal);
    const h =
      typeof door.height === "string" && door.height.trim()
        ? door.height.trim()
        : formatAsFraction(door.height_decimal);
    return `${w}" x ${h}"`;
  };

  const buildDoorOrderText = (door) => {
    const customer = customers.find((c) => c.customer_id === formData.customer_id);
    const po = (customer?.fullName || "").split(" ")[0] || "";
    const handleWord = door.handle === "left" ? "left" : "right";
    return [
      `${door.type} - ${door.color || "?"} - ${door.mesh}`,
      formatDoorDimensions(door),
      `Handle on ${handleWord}`,
      po ? `PO: ${po}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const copyDoorOrder = async (door) => {
    const text = buildDoorOrderText(door);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older iOS Safari where clipboard isn't available
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Order copied — paste into your text thread");
    } catch (ex) {
      toast.error("Couldn't copy. Long-press the text below to copy manually.");
    }
  };

  // When creating a NEW job on a customer that already has saved
  // measurements from a prior job, offer a one-tap prefill. Available
  // on both new-job-with-preselected-customer and the plain picker
  // flow (once a customer has been chosen).
  const selectedCustomer = customers.find(
    (c) => c.customer_id === formData.customer_id
  );
  const hasSavedWindowScreens =
    (selectedCustomer?.saved_window_screens || []).length > 0;
  const hasSavedScreenDoors =
    (selectedCustomer?.saved_screen_doors || []).length > 0;
  const canPrefill =
    !job && (hasSavedWindowScreens || hasSavedScreenDoors);

  const prefillSavedMeasurements = () => {
    if (!selectedCustomer) return;
    const windows = selectedCustomer.saved_window_screens || [];
    const doors = selectedCustomer.saved_screen_doors || [];
    if (windows.length) setScreenMeasurements(windows);
    if (doors.length) setScreenDoors(doors);
    const parts = [];
    if (windows.length) parts.push(`${windows.length} window screen${windows.length === 1 ? "" : "s"}`);
    if (doors.length) parts.push(`${doors.length} screen door${doors.length === 1 ? "" : "s"}`);
    toast.success(`Prefilled ${parts.join(" + ")} from the saved record`);
  };

  // --- Pick Up jobs -------------------------------------------------------
  // Fires the "Your screen is ready" SMS to the customer. Pickup
  // address is the assigned tech's `address` field (set in the Techs
  // settings page). We send through the existing /api/jobs/{id}/
  // notify-pickup-ready endpoint so Twilio call, dedupe, and the
  // messages collection write all live server-side.
  const sendPickupReadySms = async () => {
    if (!job?.job_id) {
      toast.error("Save the job first, then send the pickup SMS");
      return;
    }
    if (!formData.assignedEmployee) {
      toast.error("Assign a tech first — their address is the pickup location");
      return;
    }
    setSendingPickupSms(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.post(
        `${API}/jobs/${job.job_id}/notify-pickup-ready`,
        { instructions: pickupInstructions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPickupNotifiedAt(res.data?.sent_at || new Date().toISOString());
      toast.success("Pickup SMS sent to customer");
    } catch (ex) {
      const msg =
        ex?.response?.data?.detail ||
        "Could not send pickup SMS. Check tech address and customer phone.";
      toast.error(msg);
    } finally {
      setSendingPickupSms(false);
    }
  };

  const addMeasurement = () => {
    if (!newWidth || !newHeight) {
      toast.error("Please enter both width and height");
      return;
    }
    
    const width = parseFractionalInput(newWidth);
    const height = parseFractionalInput(newHeight);
    
    if (width === null || height === null || width <= 0 || height <= 0) {
      toast.error("Please enter valid measurements (e.g., 20, 20.5, or 20 1/4)");
      return;
    }
    
    // Check if this exact measurement already exists
    const existingIndex = screenMeasurements.findIndex(
      m => m.width === newWidth && m.height === newHeight && m.pull_tab === pullTabSide
    );
    
    if (existingIndex >= 0) {
      // Increment quantity of existing measurement
      const updated = [...screenMeasurements];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: (updated[existingIndex].quantity || 1) + 1
      };
      setScreenMeasurements(updated);
    } else {
      // Add new measurement with quantity 1
      setScreenMeasurements([
        ...screenMeasurements,
        { width: newWidth, height: newHeight, pull_tab: pullTabSide, quantity: 1 }
      ]);
    }
    
    setNewWidth("");
    setNewHeight("");
    setCutList([]); // Clear cut list when measurements change
    setShowCutList(false);
  };

  const removeMeasurement = (index) => {
    setScreenMeasurements(screenMeasurements.filter((_, i) => i !== index));
    setCutList([]); // Clear cut list when measurements change
    setShowCutList(false);
  };

  const updateQuantity = (index, delta) => {
    const updated = [...screenMeasurements];
    const newQty = (updated[index].quantity || 1) + delta;
    
    if (newQty <= 0) {
      // Remove if quantity goes to 0
      removeMeasurement(index);
    } else {
      updated[index] = { ...updated[index], quantity: newQty };
      setScreenMeasurements(updated);
      setCutList([]); // Clear cut list when measurements change
      setShowCutList(false);
    }
  };

  // Toggle completed status for a measurement
  const toggleMeasurementDone = (index) => {
    const updated = [...screenMeasurements];
    updated[index] = { 
      ...updated[index], 
      completed: !updated[index].completed 
    };
    setScreenMeasurements(updated);
  };

  // Toggle completed status for a cut list item
  const toggleCutDone = (index) => {
    const updated = [...cutList];
    updated[index] = { 
      ...updated[index], 
      completed: !updated[index].completed 
    };
    setCutList(updated);
  };

  // Get counts for progress display
  const getScreenProgress = () => {
    const total = screenMeasurements.reduce((sum, m) => sum + (m.quantity || 1), 0);
    const done = screenMeasurements.reduce((sum, m) => m.completed ? sum + (m.quantity || 1) : sum, 0);
    return { total, done };
  };

  const getCutProgress = () => {
    const total = cutList.reduce((sum, item) => sum + item.count, 0);
    const done = cutList.reduce((sum, item) => item.completed ? sum + item.count : sum, 0);
    return { total, done };
  };

  const formatMeasurement = (m) => {
    const widthStr = m.pull_tab === "width" ? `${m.width}*` : m.width;
    const heightStr = m.pull_tab === "height" ? `${m.height}*` : m.height;
    return `${widthStr}" x ${heightStr}"`;
  };

  const generateCutList = async () => {
    if (!job?.job_id) {
      // For new jobs, calculate locally
      if (screenMeasurements.length === 0) {
        toast.error("No measurements to calculate");
        return;
      }
      
      const cutCounts = {};
      screenMeasurements.forEach(m => {
        const width = parseFractionalInput(m.width);
        const height = parseFractionalInput(m.height);
        const qty = m.quantity || 1;
        
        if (width > 2 && height > 2) {
          // Round to nearest 1/16" for cut list precision
          const widthCut = Math.round((width - 2) * 16) / 16;
          const heightCut = Math.round((height - 2) * 16) / 16;
          
          // Each screen needs 2 of each cut, multiplied by quantity
          cutCounts[widthCut] = (cutCounts[widthCut] || 0) + (2 * qty);
          cutCounts[heightCut] = (cutCounts[heightCut] || 0) + (2 * qty);
        }
      });
      
      const newCutList = Object.entries(cutCounts)
        .map(([size, count]) => ({ size: parseFloat(size), count }))
        .sort((a, b) => b.size - a.size);
      
      setCutList(newCutList);
      setShowCutList(true);
      return;
    }
    
    setGeneratingCutList(true);
    try {
      const token = localStorage.getItem('session_token');
      
      // First save the current measurements
      await axios.put(
        `${API}/jobs/${job.job_id}`,
        { screen_measurements: screenMeasurements },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      // Then generate the cut list
      const response = await axios.post(
        `${API}/jobs/${job.job_id}/generate-cut-list`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      setCutList(response.data.cut_list);
      setShowCutList(true);
      toast.success(`Cut list generated: ${response.data.total_cuts} total cuts`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate cut list");
    } finally {
      setGeneratingCutList(false);
    }
  };

  // Generate PDF for Phomemo label printer (40x30mm labels)
  const handlePrintCutList = async () => {
    try {
      const customerName = customers.find(c => c.customer_id === formData.customer_id)?.fullName || 'Job';
      const filename = `cut-list-${customerName.replace(/\s+/g, '-')}.pdf`;
      
      // Create PDF - 40mm x 30mm label size (in points: 1mm = 2.83465 points)
      // Using a wider format for readability: 80mm x 60mm (can print multiple or scale)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 100] // width x height in mm
      });
      
      // Set fonts
      pdf.setFont('helvetica', 'bold');
      
      // Header
      pdf.setFontSize(14);
      pdf.text('CUT LIST', 40, 8, { align: 'center' });
      
      // Customer name
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      if (customerName && customerName !== 'Job') {
        pdf.text(customerName, 40, 14, { align: 'center' });
      }
      
      // Date
      if (formData.scheduled_date) {
        const dateStr = new Date(formData.scheduled_date).toLocaleDateString();
        pdf.setFontSize(8);
        pdf.text(dateStr, 40, 19, { align: 'center' });
      }
      
      // Divider line
      pdf.setLineWidth(0.5);
      pdf.line(5, 22, 75, 22);
      
      // Cut list items
      let yPos = 28;
      pdf.setFontSize(11);
      
      cutList.forEach((item) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${formatAsFraction(item.size)}"`, 8, yPos);
        pdf.text(`x${item.count}`, 65, yPos, { align: 'right' });
        yPos += 7;
      });
      
      // Divider line
      pdf.line(5, yPos, 75, yPos);
      yPos += 6;
      
      // Total
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      const totalCuts = cutList.reduce((sum, item) => sum + item.count, 0);
      pdf.text('TOTAL CUTS', 8, yPos);
      pdf.text(`${totalCuts}`, 65, yPos, { align: 'right' });
      
      // Note
      yPos += 8;
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'italic');
      pdf.text('* Reduced 2" for 1" corners', 40, yPos, { align: 'center' });
      
      // Get PDF as blob
      const pdfBlob = pdf.output('blob');
      
      // Check if Web Share API is available (iOS Safari)
      if (navigator.share && navigator.canShare) {
        const file = new File([pdfBlob], filename, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Cut List',
            });
            toast.success("Cut list shared!");
            return;
          } catch (shareError) {
            console.log("Share cancelled, falling back to download");
          }
        }
      }
      
      // Fallback: download the PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Cut list PDF saved! Open in Phomemo Print Master app to print.");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate cut list PDF");
    }
  };

  // Generate PDF with full screen measurements (original sizes, not reduced)
  const handlePrintMeasurements = async () => {
    try {
      const customerName = customers.find(c => c.customer_id === formData.customer_id)?.fullName || 'Job';
      const filename = `measurements-${customerName.replace(/\s+/g, '-')}.pdf`;
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 120] // width x height in mm
      });
      
      // Header
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('MEASUREMENTS', 40, 8, { align: 'center' });
      
      // Customer name
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      if (customerName && customerName !== 'Job') {
        pdf.text(customerName, 40, 14, { align: 'center' });
      }
      
      // Date
      if (formData.scheduled_date) {
        const dateStr = new Date(formData.scheduled_date).toLocaleDateString();
        pdf.setFontSize(8);
        pdf.text(dateStr, 40, 19, { align: 'center' });
      }
      
      // Divider line
      pdf.setLineWidth(0.5);
      pdf.line(5, 22, 75, 22);
      
      // Screen measurements - grouped by size with quantity (like the cut list)
      let yPos = 28;
      pdf.setFontSize(11);
      
      screenMeasurements.forEach((m) => {
        // Check if we need a new page
        if (yPos > 110) {
          pdf.addPage();
          yPos = 10;
        }
        
        const qty = m.quantity || 1;
        const widthStr = m.pull_tab === "width" ? `${m.width}*` : m.width;
        const heightStr = m.pull_tab === "height" ? `${m.height}*` : m.height;
        
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${widthStr}" x ${heightStr}"`, 8, yPos);
        pdf.text(`x${qty}`, 65, yPos, { align: 'right' });
        yPos += 7;
      });
      
      // Divider line
      yPos += 2;
      pdf.line(5, yPos, 75, yPos);
      yPos += 6;
      
      // Total
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      const totalScreens = screenMeasurements.reduce((sum, m) => sum + (m.quantity || 1), 0);
      pdf.text('TOTAL SCREENS', 8, yPos);
      pdf.text(`${totalScreens}`, 65, yPos, { align: 'right' });
      
      // Get PDF as blob
      const pdfBlob = pdf.output('blob');
      
      // Check if Web Share API is available (iOS Safari)
      if (navigator.share && navigator.canShare) {
        const file = new File([pdfBlob], filename, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Screen Measurements',
            });
            toast.success("Measurements shared!");
            return;
          } catch (shareError) {
            console.log("Share cancelled, falling back to download");
          }
        }
      }
      
      // Fallback: download the PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Measurements PDF saved!");
    } catch (error) {
      console.error("Error generating measurements PDF:", error);
      toast.error("Failed to generate measurements PDF");
    }
  };

  const currentYear = new Date().getFullYear();
  const minDate = `${currentYear}-01-01`;
  const maxDate = `${currentYear + 1}-12-31`;

  return (
    <>
    <Dialog open={open} onOpenChange={handleCloseAttempt}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="job-dialog"
        onOpenAutoFocus={(e) => {
          // Prevent Radix from auto-focusing the first form field. On
          // iOS Safari the Job Type native `<select>` was popping open
          // its picker every time the dialog mounted — very jarring.
          // Users can still tab in normally.
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-barlow text-2xl uppercase">
            {job ? "Edit Job" : "New Job"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {job ? "Edit job details" : "Create a new job"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* V3 funnel: Whole-Home Screen Check request banner. Renders when
              the customer chose Quick Check or Whole-Home Check during the
              instant estimate. "No thanks" (selected_only) intentionally has
              no banner — techs only need the affirmative signal. */}
          {job?.screen_check_preference === "whole_home_check" && (
            <div
              data-testid="screen-check-banner-whole-home"
              className="rounded-lg border-2 border-[#207024] bg-green-50 p-3 flex items-start gap-3"
            >
              <div className="mt-0.5 h-6 w-6 rounded-full bg-[#207024] text-white flex items-center justify-center flex-shrink-0 font-bold">
                <Check className="w-4 h-4" />
              </div>
              <div className="text-sm">
                <div className="font-bold text-[#207024] uppercase tracking-wider text-xs">
                  Screen Check
                </div>
                <div className="text-zinc-900 font-semibold">
                  Customer requested a Whole-Home Screen Check
                </div>
                <div className="text-zinc-600 text-xs mt-0.5">
                  Please check every window and door screen while you're there.
                </div>
              </div>
            </div>
          )}
          {job?.screen_check_preference === "quick_check" && (
            <div
              data-testid="screen-check-banner-quick"
              className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-3"
            >
              <div className="mt-0.5 h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 font-bold">
                <Check className="w-4 h-4" />
              </div>
              <div className="text-sm">
                <div className="font-bold text-amber-700 uppercase tracking-wider text-xs">
                  Screen Check
                </div>
                <div className="text-zinc-900 font-semibold">
                  Customer requested a Quick Screen Check
                </div>
                <div className="text-zinc-600 text-xs mt-0.5">
                  Note any other screens that could use attention — no pressure.
                </div>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="job-type" className="font-inter font-semibold text-zinc-700">
              Job Type
            </Label>
            <select
              id="job-type"
              data-testid="job-type-select"
              value={formData.job_type}
              onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white mt-1"
            >
              <option value="on_site">On-Site (default)</option>
              <option value="pickup">Pick Up (customer drop-off)</option>
              <option value="measure">Measure</option>
              <option value="deliver_install">Deliver / Install</option>
              <option value="reminder">Reminder</option>
            </select>
          </div>

          <div>
            <Label htmlFor="customer" className="font-inter font-semibold text-zinc-700">
              Customer *
            </Label>
            {preselectedCustomer && preselectedCustomer.customer_id ? (
              // When we already know who the job is for (from the customer
              // profile or a message thread), skip the Radix Select entirely.
              // Fall back through display candidates so we NEVER render blank
              // even if a customer's record has no fullName (unknown-name
              // conversations, legacy imports). The customer_id in formData
              // is what actually gets saved — this input is display only.
              <Input
                className="h-12 mt-1 bg-zinc-50"
                value={
                  preselectedCustomer.fullName ||
                  preselectedCustomer.phoneNumber ||
                  preselectedCustomer.phone ||
                  `Customer ${String(preselectedCustomer.customer_id).slice(-6)}`
                }
                readOnly
                data-testid="job-customer-select"
              />
            ) : (
              <Select
                value={formData.customer_id}
                onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                required
              >
                <SelectTrigger className="h-12 mt-1" data-testid="job-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.customer_id} value={customer.customer_id}>
                      {customer.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <Label htmlFor="date" className="font-inter font-semibold text-zinc-700">
              Date
            </Label>
            <Input
              id="date"
              data-testid="job-date-input"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              min={minDate}
              max={maxDate}
              className="h-12 mt-1"
            />
          </div>

          <div>
            <Label htmlFor="time" className="font-inter font-semibold text-zinc-700">
              Time
            </Label>
            <select
              id="time"
              data-testid="job-time-select"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white mt-1"
            >
              <option value="">No time</option>
              {/* 6am–6pm range, 30-minute intervals. Hours outside this
                  window were wasted space per the user's feedback — techs
                  never book before 6am or after 6pm. */}
              {Array.from({ length: 25 }, (_, i) => {
                const totalMinutes = 6 * 60 + i * 30; // start at 6:00 AM
                const hour24 = Math.floor(totalMinutes / 60);
                const minute = totalMinutes % 60;
                if (hour24 > 18) return null;
                if (hour24 === 18 && minute > 0) return null;
                const hh = hour24.toString().padStart(2, '0');
                const mm = minute.toString().padStart(2, '0');
                const time24 = `${hh}:${mm}`;
                const displayHour = hour24 === 12 ? 12 : hour24 % 12;
                const period = hour24 >= 12 ? 'PM' : 'AM';
                const display = `${displayHour}:${mm} ${period}`;
                return (
                  <option key={time24} value={time24}>
                    {display}
                  </option>
                );
              }).filter(Boolean)}
            </select>
          </div>

          <div>
            <Label htmlFor="duration" className="font-inter font-semibold text-zinc-700">
              Duration (hours)
            </Label>
            <select
              id="duration"
              data-testid="job-duration-select"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value ? parseInt(e.target.value) : "" })}
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white mt-1"
            >
              <option value="">Select duration</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((hours) => (
                <option key={hours} value={hours}>
                  {hours} hour{hours > 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="assignedEmployee" className="font-inter font-semibold text-zinc-700">
              Assigned Tech
            </Label>
            <select
              id="assignedEmployee"
              data-testid="job-tech-select"
              value={formData.assignedEmployee}
              onChange={(e) => setFormData({ ...formData, assignedEmployee: e.target.value })}
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white mt-1"
            >
              <option value="">Select tech</option>
              {techs.map((tech) => (
                <option key={tech.tech_id} value={tech.name}>
                  {tech.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="assignedEmployee2" className="font-inter font-semibold text-zinc-700">
              Second Tech (Optional)
            </Label>
            <select
              id="assignedEmployee2"
              data-testid="job-tech2-select"
              value={formData.assignedEmployee2}
              onChange={(e) => setFormData({ ...formData, assignedEmployee2: e.target.value })}
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white mt-1"
            >
              <option value="">None</option>
              {techs.filter(t => t.name !== formData.assignedEmployee).map((tech) => (
                <option key={tech.tech_id} value={tech.name}>
                  {tech.name}
                </option>
              ))}
            </select>
          </div>

          <TechCapabilityWarning
            techNames={[formData.assignedEmployee, formData.assignedEmployee2]}
            lineItems={job?.estimate_data?.line_items || preselectedCustomer?.estimate_data?.line_items}
          />

          <div>
            <Label htmlFor="status" className="font-inter font-semibold text-zinc-700">
              Status *
            </Label>
            <select
              id="status"
              data-testid="job-status-select"
              value={formData.status}
              onChange={(e) => {
                const newStatus = e.target.value;
                if (newStatus === "Needs to be Scheduled") {
                  // Same footgun guard as the calendar detail dropdown:
                  // moving to "Needs to be Scheduled" wipes date/time. Don't
                  // let it happen by accident on a real booking.
                  const hasSchedule = formData.date || formData.time;
                  if (hasSchedule) {
                    const ok = window.confirm(
                      `This will CLEAR the appointment date (${formData.date || "—"}) and time (${formData.time || "—"}).\n\nContinue?`
                    );
                    if (!ok) return;
                  }
                  setFormData({ ...formData, status: newStatus, date: "", time: "" });
                } else {
                  setFormData({ ...formData, status: newStatus });
                }
              }}
              required
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white mt-1"
            >
              <option value="Open">Open</option>
              <option value="Completed">Completed</option>
              <option value="Needs to be Scheduled">Needs to be Scheduled</option>
            </select>

            {/* Recovery banner — surfaces when a job was previously cleared
                via "Needs to be Scheduled" (which wipes date/time but now
                snapshots them into previous_date/previous_time on the
                backend). One tap restores. */}
            {job?.previous_date || job?.previous_time ? (
              <div
                data-testid="job-restore-schedule-banner"
                className="mt-3 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
              >
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-amber-900">
                    Previous schedule found
                  </div>
                  <div className="text-amber-800">
                    {job.previous_date || "—"}
                    {job.previous_time ? ` at ${job.previous_time}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="job-restore-schedule-button"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("session_token");
                      const res = await axios.post(
                        `${API}/jobs/${job.job_id}/restore-schedule`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success("Appointment restored");
                      if (onSuccess) await onSuccess(res.data?.job || {});
                      if (onOpenChange) onOpenChange(false);
                    } catch (err) {
                      toast.error(
                        err.response?.data?.detail || "Failed to restore schedule"
                      );
                    }
                  }}
                  className="shrink-0 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-wider"
                >
                  Restore
                </button>
              </div>
            ) : null}
            {job?.job_id && job?.date && (job?.status || "").toLowerCase() !== "completed" && (
              <button
                type="button"
                onClick={() => { onOpenChange?.(false); navigate(`/estimates/nearby?job_id=${job.job_id}`); }}
                className="mt-2 w-full h-9 rounded-lg border border-[#207024]/40 bg-green-50 text-[#207024] text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-green-100"
                data-testid="job-find-nearby-estimates"
              >
                <MapPin className="w-3.5 h-3.5" /> Find nearby estimates
              </button>
            )}
          </div>

          {isAdmin && formData.customer_id && formData.job_type !== "reminder" && (
            <JobQuotePicker
              customerId={formData.customer_id}
              value={formData.quote_lead_id}
              onChange={(v) => setFormData({ ...formData, quote_lead_id: v })}
              job={job}
              jobType={formData.job_type}
            />
          )}

          {/* Screen Count - Admin only */}
          {isAdmin && (
            <div>
              <Label htmlFor="screen_count" className="font-inter font-semibold text-zinc-700">
                Screen Count (Admin Only)
              </Label>
              <Input
                id="screen_count"
                data-testid="job-screen-count-input"
                type="number"
                min="0"
                value={formData.screen_count}
                onChange={(e) => setFormData({ ...formData, screen_count: e.target.value })}
                className="h-12 mt-1"
                placeholder="Number of screens"
              />
            </div>
          )}

          <div>
            <Label htmlFor="notes" className="font-inter font-semibold text-zinc-700">
              Notes / Scope of Work
            </Label>
            <Textarea
              id="notes"
              data-testid="job-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="mt-1"
              rows={3}
              placeholder="Enter scope of work details..."
            />
          </div>

          {/* Pick Up job section — only shown when the Job Type is
              "pickup". Lets the admin edit the pickup instructions that
              will be texted to the customer (default is Brett's current
              manual template) and send the "Your screen is ready" SMS
              on demand. Uses the ASSIGNED TECH's address (set in Techs
              settings) as the pickup location on the backend. */}
          {formData.job_type === "pickup" && (
            <div
              className="rounded-xl border-2 border-[#207024]/30 bg-green-50/50 p-3 space-y-3"
              data-testid="pickup-section"
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#207024] text-sm">
                  Pick Up Details
                </span>
                {pickupNotifiedAt && (
                  <span className="text-[11px] text-[#207024] bg-white px-2 py-0.5 rounded border border-[#207024]/30">
                    Sent {new Date(pickupNotifiedAt).toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-600">
                Pickup address = the assigned tech&apos;s address (edit on
                the Techs page). When you tap <strong>Text customer: Ready
                for Pickup</strong>, the customer gets:
              </p>
              <div className="bg-white rounded-lg border border-zinc-200 p-2.5 text-xs font-mono whitespace-pre-wrap text-zinc-700">
{`Your screen is ready!

Pickup Address:
{tech's address}

Instructions:
${pickupInstructions || "{instructions}"}`}
              </div>
              <div>
                <Label className="text-xs text-zinc-500">
                  Pickup Instructions (customer-facing)
                </Label>
                <Textarea
                  value={pickupInstructions}
                  onChange={(e) => setPickupInstructions(e.target.value)}
                  className="mt-1"
                  rows={3}
                  data-testid="pickup-instructions-input"
                  placeholder="Where to find the screen, access notes, etc."
                />
              </div>
              <button
                type="button"
                onClick={sendPickupReadySms}
                disabled={sendingPickupSms || !job?.job_id}
                data-testid="send-pickup-ready-btn"
                className="w-full h-11 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sendingPickupSms
                  ? "Sending..."
                  : pickupNotifiedAt
                  ? "Re-send Ready for Pickup SMS"
                  : "Text customer: Ready for Pickup"}
              </button>
              {!job?.job_id && (
                <p className="text-[11px] text-zinc-500 italic">
                  Save the job first — the SMS send needs a saved job to attach the audit record.
                </p>
              )}
            </div>
          )}

          {/* Screen Measurements Section */}
          <div className="border-t border-zinc-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Ruler className="w-5 h-5 text-[#207024]" />
                <Label className="font-inter font-semibold text-zinc-700 text-base">
                  Screen Measurements
                </Label>
              </div>
              <div className="flex items-center gap-2">
                {/* History button — recover from accidental overwrites */}
                {job?.job_id && (
                  <button
                    type="button"
                    onClick={openMeasurementHistory}
                    className="p-1.5 text-zinc-500 hover:bg-zinc-100 rounded"
                    title="View / restore previous measurements"
                    data-testid="measurement-history-btn"
                  >
                    <History className="w-4 h-4" />
                  </button>
                )}
                {/* Print measurements button */}
                {screenMeasurements.length > 0 && (
                  <button
                    type="button"
                    onClick={handlePrintMeasurements}
                    className="p-1.5 text-zinc-500 hover:bg-zinc-100 rounded"
                    title="Download measurements PDF"
                    data-testid="print-measurements-btn"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
                {/* Progress indicator */}
                {screenMeasurements.length > 0 && (
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${
                    getScreenProgress().done === getScreenProgress().total && getScreenProgress().total > 0
                      ? 'bg-green-100 text-green-700'
                      : 'bg-zinc-100 text-zinc-600'
                  }`}>
                    {getScreenProgress().done}/{getScreenProgress().total} done
                  </span>
                )}
              </div>
            </div>
            
            {/* Current measurements list */}
            {screenMeasurements.length > 0 && (
              <div className="space-y-2 mb-3">
                {screenMeasurements.map((m, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                      m.completed ? 'bg-green-50 border border-green-200' : 'bg-zinc-50'
                    }`}
                    data-testid={`measurement-${index}`}
                  >
                    <div className="flex items-center gap-2">
                      {/* Checkbox to mark as done */}
                      <button
                        type="button"
                        onClick={() => toggleMeasurementDone(index)}
                        className={`w-6 h-6 flex items-center justify-center rounded ${
                          m.completed 
                            ? 'bg-green-500 text-white' 
                            : 'bg-white border-2 border-zinc-300 hover:border-[#207024]'
                        }`}
                        data-testid={`toggle-screen-${index}`}
                      >
                        {m.completed && <Check className="w-4 h-4" />}
                      </button>
                      <span className={`font-mono text-sm font-medium ${m.completed ? 'line-through text-zinc-500' : ''}`}>
                        {formatMeasurement(m)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Quantity controls */}
                      <button
                        type="button"
                        onClick={() => updateQuantity(index, -1)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-zinc-200 hover:bg-zinc-300 text-zinc-600"
                        data-testid={`decrease-qty-${index}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-6 text-center font-semibold text-sm" data-testid={`qty-${index}`}>
                        {m.quantity || 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(index, 1)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white"
                        data-testid={`increase-qty-${index}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => removeMeasurement(index)}
                        className="text-zinc-400 hover:text-red-500 p-1 ml-1"
                        data-testid={`remove-measurement-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add new measurement */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs text-zinc-500">Width</Label>
                <Input
                  type="text"
                  placeholder='20 1/4'
                  value={newWidth}
                  onChange={(e) => setNewWidth(e.target.value)}
                  className="h-10"
                  data-testid="new-width-input"
                />
              </div>
              <span className="text-zinc-400 pb-2">x</span>
              <div className="flex-1">
                <Label className="text-xs text-zinc-500">Height</Label>
                <Input
                  type="text"
                  placeholder='40 3/8'
                  value={newHeight}
                  onChange={(e) => setNewHeight(e.target.value)}
                  className="h-10"
                  data-testid="new-height-input"
                />
              </div>
              <div className="w-20">
                <Label className="text-xs text-zinc-500">Pull Tab*</Label>
                <select
                  value={pullTabSide}
                  onChange={(e) => setPullTabSide(e.target.value)}
                  className="w-full h-10 rounded-lg border border-zinc-200 text-sm px-2"
                  data-testid="pull-tab-select"
                >
                  <option value="height">Height</option>
                  <option value="width">Width</option>
                </select>
              </div>
              <button
                type="button"
                onClick={addMeasurement}
                className="h-10 w-10 flex items-center justify-center rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white"
                data-testid="add-measurement-btn"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            
            {/* Generate Cut List Button */}
            {screenMeasurements.length > 0 && (
              <button
                type="button"
                onClick={generateCutList}
                disabled={generatingCutList}
                className="w-full mt-3 h-10 flex items-center justify-center gap-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white font-medium text-sm disabled:opacity-50"
                data-testid="generate-cut-list-btn"
              >
                <Scissors className="w-4 h-4" />
                {generatingCutList ? "Generating..." : "Generate Cut List"}
              </button>
            )}
            
            {/* Cut List Display */}
            {showCutList && cutList.length > 0 && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Scissors className="w-4 h-4 text-amber-700" />
                    <span className="font-semibold text-amber-900 text-sm">Cut List</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Print/Share button */}
                    <button
                      type="button"
                      onClick={() => setShowPrintPreview(true)}
                      className="p-1.5 text-amber-700 hover:bg-amber-100 rounded"
                      title="Print cut list"
                      data-testid="print-cut-list-btn"
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                    {/* Progress indicator (only meaningful in longest mode
                        where each row has a checkbox). */}
                    {cutMode === "longest" && (
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        getCutProgress().done === getCutProgress().total && getCutProgress().total > 0
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {getCutProgress().done}/{getCutProgress().total} cuts done
                      </span>
                    )}
                  </div>
                </div>

                {/* Sort mode toggle — segmented control. "Longest" keeps
                    the check-off flow; "Optimize" packs cuts onto 144"
                    sticks so the shop can see stick count + drop. */}
                <div className="grid grid-cols-2 gap-1 mb-2 p-1 rounded-lg bg-amber-100/60">
                  <button
                    type="button"
                    onClick={() => {
                      setCutMode("longest");
                      toast.success("Sorted longest → shortest");
                    }}
                    data-testid="cut-mode-longest"
                    className={`h-8 rounded-md text-xs font-semibold ${
                      cutMode === "longest"
                        ? "bg-white text-amber-900 shadow"
                        : "text-amber-800 hover:bg-white/50"
                    }`}
                  >
                    Longest → Shortest
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCutMode("optimize");
                      toast.success("Optimized for 144\" sticks");
                    }}
                    data-testid="cut-mode-optimize"
                    className={`h-8 rounded-md text-xs font-semibold ${
                      cutMode === "optimize"
                        ? "bg-white text-amber-900 shadow"
                        : "text-amber-800 hover:bg-white/50"
                    }`}
                  >
                    Optimize (144" sticks)
                  </button>
                </div>

                {cutMode === "longest" ? (
                  <div className="space-y-1">
                    {cutList.map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between font-mono text-sm rounded px-2 py-1.5 ${
                          item.completed ? 'bg-green-100 border border-green-200' : 'bg-white'
                        }`}
                        data-testid={`cut-list-item-${index}`}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleCutDone(index)}
                            className={`w-5 h-5 flex items-center justify-center rounded ${
                              item.completed
                                ? 'bg-green-500 text-white'
                                : 'bg-white border-2 border-amber-300 hover:border-amber-500'
                            }`}
                            data-testid={`toggle-cut-${index}`}
                          >
                            {item.completed && <Check className="w-3 h-3" />}
                          </button>
                          <span className={`font-medium ${item.completed ? 'line-through text-zinc-500' : 'text-amber-900'}`}>
                            {formatAsFraction(item.size)}"
                          </span>
                        </div>
                        <span className={item.completed ? 'text-zinc-500' : 'text-amber-700'}>x {item.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="cut-list-optimize-view">
                    <div className="flex items-center justify-between text-xs font-semibold text-amber-900 px-1">
                      <span>
                        {packedSticks.length} stick{packedSticks.length === 1 ? "" : "s"} · {formatAsFraction(totalWaste)}" drop total
                      </span>
                      <span className="text-amber-700">
                        {formatAsFraction(packedSticks.length * STICK_LENGTH - totalWaste)}" used
                      </span>
                    </div>
                    {packedSticks.map((stick, sIdx) => {
                      // Group identical cuts on a stick for compact display.
                      const counts = new Map();
                      for (const c of stick.cuts) {
                        counts.set(c, (counts.get(c) || 0) + 1);
                      }
                      const grouped = Array.from(counts.entries()).sort((a, b) => b[0] - a[0]);
                      return (
                        <div
                          key={sIdx}
                          className="bg-white rounded-md border border-amber-200 p-2"
                          data-testid={`stick-${sIdx}`}
                        >
                          <div className="flex items-center justify-between text-xs font-semibold text-amber-900 mb-1">
                            <span>Stick {sIdx + 1}</span>
                            <span className="text-amber-700">
                              Drop: {formatAsFraction(stick.remaining)}"
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 font-mono text-xs">
                            {grouped.map(([size, qty]) => (
                              <span
                                key={size}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-900"
                              >
                                {formatAsFraction(size)}"
                                {qty > 1 && <span className="text-amber-700">×{qty}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="text-xs text-amber-600 mt-2 italic">
                  * Measurements reduced by 2" for 1" corners
                </p>
              </div>
            )}

            {/* --- SCREEN DOORS ---------------------------------------
                Separate fabrication track with type/color/handle/mesh
                plus a per-door "Copy order" button so the shop can text
                each door order to the supplier in Brett's exact format:
                    Century - White - PetScreen
                    49 1/4" x 94"
                    Handle on right
                    PO: {customer first name}
            */}
            <div className="rounded-xl border border-zinc-200 bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-zinc-800 text-sm">
                  Screen Doors
                </Label>
                {canPrefill && (
                  <button
                    type="button"
                    onClick={prefillSavedMeasurements}
                    data-testid="prefill-measurements-btn"
                    className="text-[11px] font-semibold px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                    title="Load the measurements saved on this customer from a prior job"
                  >
                    Prefill from saved
                  </button>
                )}
              </div>

              {screenDoors.length > 0 && (
                <div className="space-y-2 mb-3">
                  {screenDoors.map((d, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-zinc-200 bg-zinc-50 p-2.5"
                      data-testid={`screen-door-${i}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-zinc-900">
                            {d.type} · {d.color || "?"} · {d.mesh}
                          </div>
                          <div className="font-mono text-sm text-zinc-700 mt-0.5">
                            {formatDoorDimensions(d)}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            Handle on {d.handle === "left" ? "left" : "right"}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => copyDoorOrder(d)}
                            data-testid={`copy-door-order-${i}`}
                            className="px-2 py-1 text-xs font-semibold rounded bg-[#207024] text-white hover:bg-[#1a5c1d] flex items-center gap-1"
                            title="Copy order text to clipboard"
                          >
                            <Copy className="w-3 h-3" />
                            Copy
                          </button>
                          <button
                            type="button"
                            onClick={() => removeScreenDoor(i)}
                            className="p-1 text-zinc-400 hover:text-red-500"
                            data-testid={`remove-door-${i}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add-new row — Width, Height, then meta on a second line */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-zinc-500">Width</Label>
                  <Input
                    type="text"
                    placeholder='49 1/4'
                    value={newDoor.width}
                    onChange={(e) => setNewDoor({ ...newDoor, width: e.target.value })}
                    className="h-10"
                    data-testid="new-door-width"
                  />
                </div>
                <span className="text-zinc-400 pb-2">x</span>
                <div className="flex-1">
                  <Label className="text-xs text-zinc-500">Height</Label>
                  <Input
                    type="text"
                    placeholder='94'
                    value={newDoor.height}
                    onChange={(e) => setNewDoor({ ...newDoor, height: e.target.value })}
                    className="h-10"
                    data-testid="new-door-height"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs text-zinc-500">Type</Label>
                  <select
                    value={newDoor.type}
                    onChange={(e) => setNewDoor({ ...newDoor, type: e.target.value })}
                    className="w-full h-10 rounded-lg border border-zinc-200 text-sm px-2"
                    data-testid="new-door-type"
                  >
                    <option value="Century">Century</option>
                    <option value="Laguna">Laguna</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Color</Label>
                  <Input
                    type="text"
                    placeholder="White"
                    value={newDoor.color}
                    onChange={(e) => setNewDoor({ ...newDoor, color: e.target.value })}
                    className="h-10"
                    data-testid="new-door-color"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Handle</Label>
                  <select
                    value={newDoor.handle}
                    onChange={(e) => setNewDoor({ ...newDoor, handle: e.target.value })}
                    className="w-full h-10 rounded-lg border border-zinc-200 text-sm px-2"
                    data-testid="new-door-handle"
                  >
                    <option value="right">Right</option>
                    <option value="left">Left</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Mesh</Label>
                  <select
                    value={newDoor.mesh}
                    onChange={(e) => setNewDoor({ ...newDoor, mesh: e.target.value })}
                    className="w-full h-10 rounded-lg border border-zinc-200 text-sm px-2"
                    data-testid="new-door-mesh"
                  >
                    <option value="Regular">Regular</option>
                    <option value="PetScreen">PetScreen</option>
                    <option value="Solar Screen">Solar Screen</option>
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={addScreenDoor}
                data-testid="add-screen-door-btn"
                className="w-full mt-3 h-10 flex items-center justify-center gap-1 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white text-sm font-semibold"
              >
                <Plus className="w-4 h-4" />
                Add Screen Door
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            {saveState === "failed" && saveError && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="save-error-banner">
                <X className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">Save failed</p>
                  <p className="text-xs">{saveError}</p>
                  <p className="text-xs mt-1 text-red-700">
                    Your measurements are still here. They&apos;re also saved locally on this device — tap Retry below.
                  </p>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleCloseAttempt(true)}
                data-testid="job-cancel-button"
                className="h-12 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="job-submit-button"
                className={`h-12 flex-1 rounded-lg font-semibold disabled:opacity-50 text-white ${
                  saveState === "failed"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-[#207024] hover:bg-[#1a5c1d]"
                }`}
              >
                {loading
                  ? "Saving..."
                  : saveState === "failed"
                  ? "Retry Save"
                  : job
                  ? "Update"
                  : "Create"}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    {/* Unsaved Changes Confirmation Dialog */}
    <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved measurements or changes. Are you sure you want to close without saving?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setShowCloseConfirm(false)}>
            Go Back
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirmClose}
            className="bg-red-600 hover:bg-red-700"
          >
            Discard Changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Print Cut List Modal - rendered via portal to escape Dialog focus trap */}
    {showPrintPreview && createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4">
        <div className="bg-white rounded-xl max-w-sm w-full max-h-[90vh] overflow-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h3 className="font-bold">Print Cut List</h3>
            <button
              onClick={() => setShowPrintPreview(false)}
              className="p-2 hover:bg-zinc-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Printable content - optimized for 40x30mm labels */}
          <div className="p-4">
            <div 
              ref={printCutListRef}
              className="bg-white p-4 border-2 border-zinc-300 rounded"
              style={{ width: '280px', margin: '0 auto' }}
            >
              {/* Header */}
              <div className="text-center border-b-2 border-zinc-900 pb-2 mb-3">
                <div className="font-black text-lg uppercase tracking-wide">CUT LIST</div>
                {formData.customer_id && (
                  <div className="text-sm font-semibold text-zinc-700">
                    {customers.find(c => c.customer_id === formData.customer_id)?.fullName || ''}
                  </div>
                )}
                {formData.scheduled_date && (
                  <div className="text-xs text-zinc-500">
                    {new Date(formData.scheduled_date).toLocaleDateString()}
                  </div>
                )}
              </div>
              
              {/* Cut list items */}
              <div className="space-y-1 font-mono text-sm">
                {cutList.map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b border-dashed border-zinc-300">
                    <span className="font-bold">{formatAsFraction(item.size)}"</span>
                    <span className="font-black text-lg">x{item.count}</span>
                  </div>
                ))}
              </div>
              
              {/* Total */}
              <div className="mt-3 pt-2 border-t-2 border-zinc-900 flex justify-between items-center">
                <span className="font-bold">TOTAL CUTS</span>
                <span className="font-black text-xl">{cutList.reduce((sum, item) => sum + item.count, 0)}</span>
              </div>
              
              {/* Note */}
              <div className="mt-2 text-[10px] text-zinc-500 text-center italic">
                * Reduced 2" for 1" corners
              </div>
            </div>
            
            <p className="text-xs text-zinc-500 mt-4 text-center">
              Save and open in Phomemo Print Master app
            </p>
          </div>
          
          {/* Actions */}
          <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3">
            <button
              type="button"
              onClick={() => setShowPrintPreview(false)}
              className="flex-1 px-4 py-2.5 border border-zinc-300 rounded-lg font-semibold"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handlePrintCutList}
              className="flex-1 px-4 py-2.5 bg-[#207024] text-white rounded-lg font-semibold flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              Save / Share
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Draft Restore Prompt — surfaces if localStorage has typed-in
        measurements from a previous session that never made it to the server */}
    <AlertDialog open={draftRestoreOpen} onOpenChange={setDraftRestoreOpen}>
      <AlertDialogContent data-testid="draft-restore-prompt">
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved measurements found</AlertDialogTitle>
          <AlertDialogDescription>
            {pendingDraft?.measurements?.length || 0} measurement
            {(pendingDraft?.measurements?.length || 0) === 1 ? "" : "s"} from your last session
            {pendingDraft?.saved_at
              ? ` (${new Date(pendingDraft.saved_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })})`
              : ""}
            {" "}were never saved to the server. Would you like to restore them?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={discardDraftMeasurements} data-testid="draft-discard-btn">
            Discard
          </AlertDialogCancel>
          <AlertDialogAction onClick={restoreDraftMeasurements} data-testid="draft-restore-btn">
            Restore
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Measurement History Modal */}
    <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
      <DialogContent className="max-w-md" data-testid="measurement-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#207024]" />
            Measurement History
          </DialogTitle>
          <DialogDescription>
            Restore measurements from a previous save. Up to 10 snapshots are kept.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
          {historyLoading ? (
            <p className="text-sm text-zinc-500 py-6 text-center">Loading…</p>
          ) : historySnapshots.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-sm text-zinc-500">No previous snapshots saved for this job yet.</p>
              <p className="text-xs text-zinc-400 mt-2">
                Any future save that changes measurements will automatically create a snapshot here.
              </p>
            </div>
          ) : (
            <ul className="space-y-2 py-2">
              {historySnapshots.map((snap, i) => {
                const count = (snap.measurements || []).length;
                const when = snap.saved_at ? new Date(snap.saved_at) : null;
                const whenStr = when ? when.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "unknown date";
                return (
                  <li key={i} className="border border-zinc-200 rounded-lg p-3" data-testid={`history-snapshot-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-zinc-900">
                          {count} measurement{count === 1 ? "" : "s"}
                        </p>
                        <p className="text-xs text-zinc-500 mt-0.5">{whenStr}</p>
                        {snap.replaced_by_user && (
                          <p className="text-xs text-zinc-500">Saved by {snap.replaced_by_user}</p>
                        )}
                        {snap.note && (
                          <p className="text-xs text-amber-600 mt-1 italic">{snap.note}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => restoreMeasurementSnapshot(i)}
                        disabled={restoringIndex !== null}
                        className="shrink-0 h-8 px-3 rounded-md bg-[#207024] hover:bg-[#1a5c1d] text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                        data-testid={`restore-snapshot-${i}-btn`}
                      >
                        <RotateCcw className="w-3 h-3" />
                        {restoringIndex === i ? "Restoring…" : "Restore"}
                      </button>
                    </div>
                    {count > 0 && count <= 6 && (
                      <div className="mt-2 text-[11px] text-zinc-500 grid grid-cols-2 gap-x-2 gap-y-0.5 max-h-24 overflow-y-auto">
                        {snap.measurements.map((m, mi) => (
                          <div key={mi} className="truncate">
                            {(m.location || `#${mi + 1}`)}: {m.width}×{m.height}
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
